import React, { useState, useEffect } from 'react';
import {
  Col,
  Row,
  Input,
  ModalHeader,
  ModalBody,
  Label,
  ModalFooter,
  Modal,
  Form,
  FormFeedback,
} from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { addNewProperty, editProperty } from '../../services/propertyService';
import { removeFile, uploadFile } from 'services/fileService';
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';
import { Property, TypeOfAcquisition } from 'utility/interface';
import { FilePreview } from 'Components/Common/FilePreview';
import { uploadFieldCategoryMap } from 'Components/constants/uploadFieldCategoryMap';

type UploadedFile = {
  url: string;
  uploadedAt: number;
};

type UploadField =
  | 'propertyCard'
  | 'ptrDocument'
  | 'houseTaxBill'
  | 'acquisitionDocument'
  | 'nonAgriculturalCertificate'
  | 'constructionPermissions'
  | 'buildingPlanApprovals'
  | 'feePaymentReceipts'
  | 'environmentalClearance'
  | 'fireSafetyCertificate'
  | 'photos';

interface CreatePropertyModelProps {
  isOpen: boolean;

  toggle: () => void;

  organizationId: number;

  organizationName: string;

  trustRegNumber?: string;

  surveyNumber?: string;

  landName?: string;

  landId?: number;

  onSuccess: () => void;

  editData?;
}

const CreatePropertyModel = ({
  isOpen,
  toggle,
  organizationId,

  organizationName,

  trustRegNumber,

  surveyNumber,

  landName,

  landId,
  onSuccess,
  editData,
}: CreatePropertyModelProps) => {
  const [uploads, setUploads] = useState<Record<UploadField, UploadedFile[]>>({
    propertyCard: [],
    ptrDocument: [],
    houseTaxBill: [],
    acquisitionDocument: [],
    nonAgriculturalCertificate: [],
    constructionPermissions: [],
    buildingPlanApprovals: [],
    feePaymentReceipts: [],
    environmentalClearance: [],
    fireSafetyCertificate: [],
    photos: [],
  });

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Please Enter Property Name'),
    address: Yup.string().required('Please Enter Address'),
    trustRegNumber: Yup.string(),
    size: Yup.number()
      .integer('Size must be a whole number') // ✅ disallows decimals
      .positive('Size must be greater than 0') // ✅ only positive
      .required('Size is required'),
    floor: Yup.number()
      .integer()
      .min(0, 'Must be 0 or positive')
      .required('Number of floors is required'),
    cellar: Yup.boolean(),
    tenantsCount: Yup.number()
      .integer()
      .positive()
      .required('Number of tenants is required'),
    typeOfAcquisition: Yup.string()
      .oneOf(Object.values(TypeOfAcquisition), 'Invalid Acquisition Type')
      .required('Type of Acquisition is required'),
    acquisitionDocument: Yup.string(),
    propertyCard: Yup.string(),
    minutesOfMeeting: Yup.string(),
    PTR: Yup.string(),
    houseTaxBill: Yup.string(),
    nonAgriculturalCertificate: Yup.string(),
    constructionPermissions: Yup.string(),
    buildingPlanApprovals: Yup.string(),
    feePaymentReceipts: Yup.string(),
    environmentalClearance: Yup.string(),
    fireSafetyCertificate: Yup.string(),
    photos: Yup.string(),
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      address: '',
      trustRegNumber: '',
      size: undefined,
      floor: 1,
      tenantsCount: 1,
      cellar: false,
      typeOfAcquisition: undefined,
      acquisitionDocument: '',
      propertyCard: '',
      minutesOfMeeting: '',
      PTR: '',
      houseTaxBill: '',
      photos: '',
    },
  });

  const formReset = () => {
    reset({
      name: editData?.name ?? '',
      address: editData?.address ?? '',
      trustRegNumber: editData?.trustRegNumber ?? '',
      size: editData?.size ?? null,
      typeOfAcquisition: editData?.typeOfAcquisition ?? null,
      floor: editData?.floor ?? 1,
      tenantsCount: editData?.tenantsCount ?? 1,
      cellar: editData?.cellar ?? false,
      minutesOfMeeting: editData?.minutesOfMeeting ?? '',
    });
  };

  const dispatch = useDispatch();

  const handleMultiUpload = async (
    files: FileList | null,
    field: UploadField,
  ) => {
    if (!files || !files.length) return;

    dispatch(isVisible(true));
    try {
      const fileArr = Array.from(files);

      const values = getValues();

      const results = await Promise.all(
        fileArr.map(file =>
          uploadFile({
            file,

            category: uploadFieldCategoryMap[field],

            customFileName: field,

            organizationName,

            trustRegNumber,

            surveyNumber,

            propertyName: values.name || landName,
          }),
        ),
      );

      const newFiles: UploadedFile[] = results.map(r => ({
        url: r.data.url,
        uploadedAt: Date.now(),
      }));

      setUploads(prev => {
        const updated = [...newFiles, ...prev[field]] // ⬅ DESC ORDER
          .sort((a, b) => b.uploadedAt - a.uploadedAt);

        setValue(field as any, updated.map(f => f.url).join(','));
        return { ...prev, [field]: updated };
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      dispatch(isVisible(false));
    }
  };

  const removeUploadedFile = async (field: UploadField, index: number) => {
    const file = uploads[field][index];

    setUploads(prev => {
      const updated = prev[field].filter((_, i) => i !== index);
      setValue(field as any, updated.map(f => f.url).join(','));
      return { ...prev, [field]: updated };
    });

    if (file?.url) {
      try {
        await removeFile([file.url]);
      } catch (e) {
        console.error('Delete failed', e);
      }
    }
  };

  // Effect to populate form when editing
  useEffect(() => {
    if (editData) {
      formReset();

      // Set image states
      if (editData.propertyCard) {
        const propertyCard = editData.propertyCard.split(',');
        setUploads(prev => ({
          ...prev,
          propertyCard: propertyCard.map(url => ({
            url,
            uploadedAt: Date.now(),
          })),
        }));
      }
      if (editData.photos) {
        const photos = editData.photos.split(',');
        setUploads(prev => ({
          ...prev,
          photos: photos.map(url => ({ url, uploadedAt: Date.now() })),
        }));
      }
      if (editData.PTR) {
        const ptr = editData.PTR.split(',');
        setUploads(prev => ({
          ...prev,
          ptrDocument: ptr.map(url => ({ url, uploadedAt: Date.now() })),
        }));
      }
      if (editData.houseTaxBill) {
        const houseTaxBill = editData.houseTaxBill.split(',');
        setUploads(prev => ({
          ...prev,
          houseTaxBill: houseTaxBill.map(url => ({
            url,
            uploadedAt: Date.now(),
          })),
        }));
      }
      if (editData.acquisitionDocument) {
        const acquisitionDocument = editData.acquisitionDocument.split(',');
        setUploads(prev => ({
          ...prev,
          acquisitionDocument: acquisitionDocument.map(url => ({
            url,
            uploadedAt: Date.now(),
          })),
        }));
      }
      if (editData.nonAgriculturalCertificate) {
        const nonAgriculturalCertificate =
          editData.nonAgriculturalCertificate.split(',');
        setUploads(prev => ({
          ...prev,
          nonAgriculturalCertificate: nonAgriculturalCertificate.map(url => ({
            url,
            uploadedAt: Date.now(),
          })),
        }));
      }
      if (editData.constructionPermissions) {
        const constructionPermissions =
          editData.constructionPermissions.split(',');
        setUploads(prev => ({
          ...prev,
          constructionPermissions: constructionPermissions.map(url => ({
            url,
            uploadedAt: Date.now(),
          })),
        }));
      }
      if (editData.buildingPlanApprovals) {
        const buildingPlanApprovals = editData.buildingPlanApprovals.split(',');
        setUploads(prev => ({
          ...prev,
          buildingPlanApprovals: buildingPlanApprovals.map(url => ({
            url,
            uploadedAt: Date.now(),
          })),
        }));
      }
      if (editData.feePaymentReceipts) {
        const feePaymentReceipts = editData.feePaymentReceipts.split(',');
        setUploads(prev => ({
          ...prev,
          feePaymentReceipts: feePaymentReceipts.map(url => ({
            url,
            uploadedAt: Date.now(),
          })),
        }));
      }
      if (editData.environmentalClearance) {
        const environmentalClearance =
          editData.environmentalClearance.split(',');
        setUploads(prev => ({
          ...prev,
          environmentalClearance: environmentalClearance.map(url => ({
            url,
            uploadedAt: Date.now(),
          })),
        }));
      }
      if (editData.fireSafetyCertificate) {
        const fireSafetyCertificate = editData.fireSafetyCertificate.split(',');
        setUploads(prev => ({
          ...prev,
          fireSafetyCertificate: fireSafetyCertificate.map(url => ({
            url,
            uploadedAt: Date.now(),
          })),
        }));
      }
    }
  }, [isOpen, editData, reset]);

  const onSubmit = async values => {
    dispatch(isVisible(true));
    try {
      const propertyData: Omit<Property, 'id'> = {
        name: values.name,
        address: values.address,
        trustRegNumber: values.trustRegNumber,
        assesseeNumber: values.assesseeNumber,
        size: values.size,
        floor: values.floor,
        tenantsCount: values.tenantsCount,
        cellar: values.cellar,
        stampDuty: values.stampDuty,
        typeOfAcquisition: values.typeOfAcquisition,
        acquisitionDocument: uploads.acquisitionDocument
          .map(f => f.url)
          .join(','),
        propertyCard: uploads.propertyCard.map(f => f.url).join(','),
        PTR: uploads.ptrDocument.map(f => f.url).join(','),
        houseTaxBill: uploads.houseTaxBill.map(f => f.url).join(','),
        nonAgriculturalCertificate: uploads.nonAgriculturalCertificate
          .map(f => f.url)
          .join(','),
        constructionPermissions: uploads.constructionPermissions
          .map(f => f.url)
          .join(','),
        buildingPlanApprovals: uploads.buildingPlanApprovals
          .map(f => f.url)
          .join(','),
        feePaymentReceipts: uploads.feePaymentReceipts
          .map(f => f.url)
          .join(','),
        environmentalClearance: uploads.environmentalClearance
          .map(f => f.url)
          .join(','),
        fireSafetyCertificate: uploads.fireSafetyCertificate
          .map(f => f.url)
          .join(','),
        minutesOfMeeting: values.minutesOfMeeting,
        photos: uploads.photos.map(f => f.url).join(','),
        lastRenovation: values.lastRenovation,
        organizationId: organizationId || values.organizationId,
        landId: landId || values.landId,
      };

      if (propertyData.photos.trim() === '') {
        propertyData.photos = null;
      }

      if (editData) {
        const result = await editProperty(editData.id, propertyData);
        if (result?.data) {
          toast.success('Property Updated Successfully', { autoClose: 2000 });
        }
      } else {
        const result = await addNewProperty(propertyData);
        if (result?.data) {
          toast.success('Property Added Successfully', { autoClose: 2000 });
        }
      }

      onSuccess();
      formReset();
      toggle();
    } catch (err) {
      console.error(err?.response?.data?.message || err?.message);
      toast.error(err?.response?.data?.message || err?.message, {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
    }
  };

  // Cleanup preview URLs when component unmounts or when modal closes
  useEffect(() => {
    return () => {
      setUploads({
        propertyCard: [],
        ptrDocument: [],
        houseTaxBill: [],
        acquisitionDocument: [],
        nonAgriculturalCertificate: [],
        constructionPermissions: [],
        buildingPlanApprovals: [],
        feePaymentReceipts: [],
        environmentalClearance: [],
        fireSafetyCertificate: [],
        photos: [],
      });
    };
  }, [isOpen]);

  // Reset previews when modal closes
  useEffect(() => {
    if (!isOpen) {
      setUploads({
        propertyCard: [],
        ptrDocument: [],
        houseTaxBill: [],
        acquisitionDocument: [],
        nonAgriculturalCertificate: [],
        constructionPermissions: [],
        buildingPlanApprovals: [],
        feePaymentReceipts: [],
        environmentalClearance: [],
        fireSafetyCertificate: [],
        photos: [],
      });
    }
  }, [isOpen]);

  return (
    <Modal
      id="showModal"
      isOpen={isOpen}
      toggle={() => {
        formReset();
        toggle();
      }}
      backdrop="static"
      centered
      size="lg"
    >
      <ModalHeader
        className="bg-light p-3"
        toggle={() => {
          formReset();
          toggle();
        }}
      >
        {editData ? 'Edit Property' : 'Add Property'}
      </ModalHeader>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody className="overflow-hidden">
          <Row className="g-3">
            <h5 className="mb-3">General Details</h5>

            {/* Property Name */}
            <Col lg={6}>
              <Label htmlFor="name-field" className="form-label">
                Property Name *
              </Label>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="name-field"
                    placeholder="Enter Property Name"
                    invalid={!!errors.name}
                  />
                )}
              />
              {errors.name && (
                <FormFeedback>{errors.name.message}</FormFeedback>
              )}
            </Col>

            {/* Trust Reg Number */}
            <Col lg={6}>
              <Label htmlFor="trustRegNumber-field" className="form-label">
                Trust Registration Number
              </Label>
              <Controller
                name="trustRegNumber"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="trustRegNumber-field"
                    placeholder="Enter Trust Registration Number"
                    invalid={!!errors.trustRegNumber}
                  />
                )}
              />
              {errors.trustRegNumber && (
                <FormFeedback>{errors.trustRegNumber.message}</FormFeedback>
              )}
            </Col>

            {/* Acquisition */}
            <Col lg={6}>
              <Label htmlFor="typeOfAcquisition-field" className="form-label">
                Type of Acquisition *
              </Label>
              <Controller
                name="typeOfAcquisition"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="select"
                    id="typeOfAcquisition-field"
                    invalid={!!errors.typeOfAcquisition}
                  >
                    <option value="">Select type of Acquisition</option>
                    {Object.values(TypeOfAcquisition).map(v => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </Input>
                )}
              />
              {errors.typeOfAcquisition && (
                <FormFeedback>{errors.typeOfAcquisition.message}</FormFeedback>
              )}
            </Col>

            {/* Address */}
            <Col lg={6}>
              <Label htmlFor="address-field" className="form-label">
                Address *
              </Label>
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="address-field"
                    placeholder="Enter Address"
                    invalid={!!errors.address}
                  />
                )}
              />
              {errors.address && (
                <FormFeedback>{errors.address.message}</FormFeedback>
              )}
            </Col>

            {/* Minutes of Meeting */}
            <Col lg={12}>
              <Label htmlFor="minutesOfMeeting-field" className="form-label">
                Minutes of Meeting
              </Label>
              <Controller
                name="minutesOfMeeting"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="textarea"
                    id="minutesOfMeeting-field"
                    placeholder="Enter minutes of meeting"
                  />
                )}
              />
              {errors.minutesOfMeeting && (
                <FormFeedback>{errors.minutesOfMeeting.message}</FormFeedback>
              )}
            </Col>

            <hr className="my-4" />
            <h5 className="mb-3">Basic Specifications</h5>

            {/* Floors */}
            <Col lg={6}>
              <Label htmlFor="floor-field" className="form-label">
                No. of floors (Including ground floor) *
              </Label>
              <Controller
                name="floor"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    id="floor-field"
                    placeholder="Enter the no. of floors present in the property"
                    invalid={!!errors.floor}
                  />
                )}
              />
              {errors.floor && (
                <FormFeedback>{errors.floor.message}</FormFeedback>
              )}
            </Col>

            {/* Tenants */}
            <Col lg={6}>
              <Label htmlFor="tenants-field" className="form-label">
                No. of Tenants *
              </Label>
              <div
                style={{
                  display: 'flex',
                  alignContent: 'center',
                  justifyContent: 'center',
                }}
              >
                <Controller
                  name="tenantsCount"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      id="tenants-field"
                      placeholder="Enter number of tenants"
                      invalid={!!errors.tenantsCount}
                    />
                  )}
                />
                {errors.tenantsCount && (
                  <FormFeedback>{errors.tenantsCount.message}</FormFeedback>
                )}
              </div>
            </Col>

            {/* Size */}
            <Col lg={6}>
              <Label htmlFor="size-field" className="form-label">
                Size (Sq. ft) *
              </Label>
              <Controller
                name="size"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    id="size-field"
                    placeholder="Enter property size"
                    invalid={!!errors.size}
                  />
                )}
              />
              {errors.size && (
                <FormFeedback>{errors.size.message}</FormFeedback>
              )}
            </Col>

            {/* Cellar */}
            <Col lg={6}>
              <Label htmlFor="cellar-field" className="form-label">
                Is cellar present?
              </Label>
              <div
                style={{
                  display: 'flex',
                  alignContent: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                <Controller
                  name="cellar"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <Input
                      {...field}
                      type="checkbox"
                      id="cellar-field"
                      checked={!!value}
                      invalid={!!errors.cellar}
                      onChange={e => onChange(e.target.checked)}
                      style={{ height: '20px', width: '20px' }}
                    />
                  )}
                />
                {errors.cellar && (
                  <FormFeedback>{errors.cellar.message}</FormFeedback>
                )}
              </div>
            </Col>

            <hr className="my-4" />
            <h5 className="mb-3">Documents</h5>

            <Col lg={6}>
              <Label htmlFor="propertyCard-field" className="form-label">
                Property Card
              </Label>
              <Input
                type="file"
                id="propertyCard-field"
                className="form-control"
                multiple
                onChange={e => {
                  handleMultiUpload(e.target.files, 'propertyCard');
                }}
              />
              <FilePreview
                files={uploads.propertyCard}
                onRemove={i => removeUploadedFile('propertyCard', i)}
              />
            </Col>

            <Col lg={6}>
              <Label htmlFor="PTR-field" className="form-label">
                PTR Document
              </Label>
              <Input
                type="file"
                id="PTR-field"
                className="form-control"
                multiple
                onChange={e => {
                  handleMultiUpload(e.target.files, 'ptrDocument');
                }}
              />
              <FilePreview
                files={uploads.ptrDocument}
                onRemove={i => removeUploadedFile('ptrDocument', i)}
              />
            </Col>

            <Col lg={6}>
              <Label htmlFor="houseTaxBill-field" className="form-label">
                House Tax Bill
              </Label>
              <Input
                type="file"
                id="houseTaxBill-field"
                className="form-control"
                multiple
                onChange={e => {
                  handleMultiUpload(e.target.files, 'houseTaxBill');
                }}
              />
              <FilePreview
                files={uploads.houseTaxBill}
                onRemove={i => removeUploadedFile('houseTaxBill', i)}
              />
            </Col>

            <Col lg={6}>
              <Label htmlFor="acquisitionDocument-field" className="form-label">
                Acqusition Document
              </Label>
              <Input
                type="file"
                id="acquisitionDocument-field"
                className="form-control"
                multiple
                onChange={e => {
                  handleMultiUpload(e.target.files, 'acquisitionDocument');
                }}
              />
              <FilePreview
                files={uploads.acquisitionDocument}
                onRemove={i => removeUploadedFile('acquisitionDocument', i)}
              />
            </Col>

            <Col lg={6}>
              <Label
                htmlFor="nonAgriculturalCertificate-field"
                className="form-label"
              >
                Non-Agricultural (NA) Certificate
              </Label>
              <Input
                type="file"
                id="nonAgriculturalCertificate-field"
                className="form-control"
                multiple
                onChange={e => {
                  handleMultiUpload(
                    e.target.files,
                    'nonAgriculturalCertificate',
                  );
                }}
              />
              <FilePreview
                files={uploads.nonAgriculturalCertificate}
                onRemove={i =>
                  removeUploadedFile('nonAgriculturalCertificate', i)
                }
              />
            </Col>

            <Col lg={6}>
              <Label
                htmlFor="constructionPermissions-field"
                className="form-label"
              >
                Construction/Renovation Permissions
              </Label>
              <Input
                type="file"
                id="constructionPermissions-field"
                className="form-control"
                multiple
                onChange={e => {
                  handleMultiUpload(e.target.files, 'constructionPermissions');
                }}
              />
              <FilePreview
                files={uploads.constructionPermissions}
                onRemove={i => removeUploadedFile('constructionPermissions', i)}
              />
            </Col>

            <Col lg={6}>
              <Label
                htmlFor="buildingPlanApprovals-field"
                className="form-label"
              >
                Building Plan Approvals
              </Label>
              <Input
                type="file"
                id="buildingPlanApprovals-field"
                className="form-control"
                multiple
                onChange={e => {
                  handleMultiUpload(e.target.files, 'buildingPlanApprovals');
                }}
              />
              <FilePreview
                files={uploads.buildingPlanApprovals}
                onRemove={i => removeUploadedFile('buildingPlanApprovals', i)}
              />
            </Col>

            <Col lg={6}>
              <Label htmlFor="feePaymentReceipts-field" className="form-label">
                Fee Payment Receipts
              </Label>
              <Input
                type="file"
                id="feePaymentReceipts-field"
                className="form-control"
                multiple
                onChange={e => {
                  handleMultiUpload(e.target.files, 'feePaymentReceipts');
                }}
              />
              <FilePreview
                files={uploads.feePaymentReceipts}
                onRemove={i => removeUploadedFile('feePaymentReceipts', i)}
              />
            </Col>

            <Col lg={6}>
              <Label
                htmlFor="environmentalClearance-field"
                className="form-label"
              >
                Environmental Clearance
              </Label>
              <Input
                type="file"
                id="environmentalClearance-field"
                className="form-control"
                multiple
                onChange={e => {
                  handleMultiUpload(e.target.files, 'environmentalClearance');
                }}
              />
              <FilePreview
                files={uploads.environmentalClearance}
                onRemove={i => removeUploadedFile('environmentalClearance', i)}
              />
            </Col>

            <Col lg={6}>
              <Label
                htmlFor="fireSafetyCertificate-field"
                className="form-label"
              >
                Fire Safety Certificate
              </Label>
              <Input
                type="file"
                id="fireSafetyCertificate-field"
                className="form-control"
                multiple
                onChange={e => {
                  handleMultiUpload(e.target.files, 'fireSafetyCertificate');
                }}
              />
              <FilePreview
                files={uploads.fireSafetyCertificate}
                onRemove={i => removeUploadedFile('fireSafetyCertificate', i)}
              />
            </Col>

            <Col lg={12}>
              <Label htmlFor="photos-field" className="form-label">
                Photos
              </Label>
              <Input
                type="file"
                id="photos-field"
                className="form-control"
                multiple
                onChange={e => {
                  handleMultiUpload(e.target.files, 'photos');
                }}
              />
              <FilePreview
                files={uploads.photos}
                onRemove={i => removeUploadedFile('photos', i)}
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <div className="hstack gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => {
                formReset();
                toggle();
              }}
            >
              Close
            </button>
            <button type="submit" className="btn btn-success">
              {editData ? 'Update' : 'Create'}
            </button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CreatePropertyModel;
